import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { gcoreServerUrl } from '../../config';
import { productNameFromFolderName } from './SubMatFolderSpecDialog';
import * as subMatFolderActions from '../../store/subMatFolder';
import { bulkCreateDirect as saveFolderAlarms } from '../../store/subMatFolderAlarmEmail';

export const ATTR_FILE_NAME = '_속성.txt';

const parseJson = (value) => {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch (_err) {
    return undefined;
  }
};

export const parseAttrText = (text) => {
  const multi = parseAttrMulti(text);
  const map = {};
  Object.entries(multi).forEach(([key, values]) => {
    if (values.length) map[key] = values[values.length - 1];
  });
  return map;
};

export const parseAttrMulti = (text) => {
  const map = {};
  String(text || '').split(/\r?\n/).forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) return;
    const key = trimmed.slice(0, eq).trim();
    const value = trimmed.slice(eq + 1).trim();
    if (!map[key]) map[key] = [];
    map[key].push(value);
  });
  return map;
};

const splitFirst = (value) => {
  const text = String(value || '');
  const bar = text.indexOf('|');
  if (bar < 0) return [text, ''];
  return [text.slice(0, bar), text.slice(bar + 1)];
};

const firstByName = (lines) => {
  const map = {};
  (lines || []).forEach((line) => {
    const [name, rest] = splitFirst(line);
    if (name && map[name] == null) map[name] = rest;
  });
  return map;
};

/** 속성 파일의 product 상대 경로를 묶음 안의 폴더 경로로 푼다. */
export const resolveFolderPath = (fromPath, relative) => {
  const parts = String(fromPath || '').split('/').filter(Boolean);
  String(relative || '').split('/').forEach((seg) => {
    if (!seg || seg === '.') return;
    if (seg === '..') parts.pop();
    else parts.push(seg);
  });
  return parts.join('/');
};

const specFromAttr = (attr, folderName, productFolderId) => {
  const cert = attr.cert || '';
  const role = attr.role || '';
  if (role === 'general') {
    return attr.generalDoc ? { g04GeneralDocuId: attr.generalDoc } : null;
  }
  if (role === 'certification') {
    return cert ? { g04docuGCertificationId: cert } : null;
  }
  if (role === 'product') {
    const productName = productNameFromFolderName(folderName);
    if (!cert || !productName) return null;
    return { g04docuGCertificationId: cert, productName };
  }
  if (role === 'report') {
    if (!cert || !attr.thickness || !productFolderId) return null;
    return {
      g04docuGCertificationId: cert,
      thickness: attr.thickness,
      productFolderId,
    };
  }
  if (role === 'slot') {
    const materialSlotValues = parseJson(attr.materialSlotValues);
    if (!cert || !Array.isArray(materialSlotValues)) return null;
    return { g04docuGCertificationId: cert, materialSlotValues };
  }
  if (role === 'film') {
    if (!cert || !attr.thickness) return null;
    return {
      g04docuGCertificationId: cert,
      thickness: attr.thickness,
      ...(attr.filmThicknessItemId ? { filmThicknessItemId: attr.filmThicknessItemId } : {}),
    };
  }
  if (role === 'process') {
    const glass = parseJson(attr.glass);
    if (!cert || !glass) return null;
    const classificationParts = parseJson(attr.classification);
    return {
      g04docuGCertificationId: cert,
      glass,
      ...(attr.code ? { code: attr.code } : {}),
      ...(attr.codeByStandard ? { codeByStandard: attr.codeByStandard } : {}),
      ...(attr.thickness ? { thickness: attr.thickness } : {}),
      ...(Array.isArray(classificationParts) ? { classificationParts } : {}),
    };
  }
  if (role === 'grade') {
    const classificationParts = parseJson(attr.classification);
    if (!cert) return null;
    return {
      g04docuGCertificationId: cert,
      ...(attr.thickness ? { thickness: attr.thickness } : {}),
      ...(Array.isArray(classificationParts) ? { classificationParts } : {}),
    };
  }
  return null;
};

const parentPathOf = (path) => {
  const index = path.lastIndexOf('/');
  return index < 0 ? '' : path.slice(0, index);
};

const nameOf = (path) => {
  const index = path.lastIndexOf('/');
  return index < 0 ? path : path.slice(index + 1);
};

export async function downloadFolderBundle(folder) {
  const response = await window.fetch(`${gcoreServerUrl}/api/subMatFolders/${folder.id}/bundle`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || '폴더를 받지 못했습니다.');
  }
  const blob = await response.blob();
  if (!blob.size) {
    throw new Error('폴더를 받지 못했습니다.');
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${folder.name || 'folder'}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const dirsFromFileList = (fileList, warnings) => {
  const dirs = new Map();
  Array.from(fileList || []).forEach((file) => {
    const raw = file.webkitRelativePath || file.name || '';
    const path = raw.replaceAll('\\', '/').replace(/^\/+|\/+$/g, '');
    if (!path || path.split('/').includes('..')) return;
    const parts = path.split('/');
    if (parts.length < 2) return;
    for (let i = 1; i < parts.length; i += 1) {
      const dirPath = parts.slice(0, i).join('/');
      if (!dirs.has(dirPath)) dirs.set(dirPath, { pdfs: [], attr: null });
    }
    const dirPath = parts.slice(0, -1).join('/');
    const fileName = parts[parts.length - 1];
    const node = dirs.get(dirPath);
    if (fileName === ATTR_FILE_NAME) node.attr = file;
    else if (fileName.toLowerCase().endsWith('.pdf')) node.pdfs.push(file);
    else if (!fileName.startsWith('.')) warnings.push(`${path} 은 PDF가 아니라 넣지 않았습니다.`);
  });
  return dirs;
};

export const FOLDER_DROP_UNSUPPORTED_MESSAGE = '이 브라우저에서는 폴더를 끌어다 놓을 수 없습니다. 폴더 업로드나 zip 업로드를 사용해 주세요.';

const addBundleFile = (node, path, name, file, warnings) => {
  if (name === ATTR_FILE_NAME) node.attr = file;
  else if (name.toLowerCase().endsWith('.pdf')) node.pdfs.push(file);
  else if (!name.startsWith('.')) warnings.push(`${path}/${name} 은 PDF가 아니라 넣지 않았습니다.`);
};

const isSafeEntryName = (name) => Boolean(name) && name !== '.' && name !== '..' && !name.includes('/') && !name.includes('\\');

/** 탐색기에서 떨어뜨린 폴더. webkitGetAsEntry는 드롭 처리 안에서 바로 호출해야 한다. */
const readEntryChildren = (dirEntry) => new Promise((resolve, reject) => {
  const reader = dirEntry.createReader();
  const all = [];
  const readBatch = () => {
    reader.readEntries((batch) => {
      if (!batch?.length) {
        resolve(all);
        return;
      }
      all.push(...batch);
      readBatch();
    }, reject);
  };
  readBatch();
});

const fileFromEntry = (fileEntry) => new Promise((resolve, reject) => {
  fileEntry.file(resolve, reject);
});

const collectDirectoryEntry = async (dirEntry) => {
  const warnings = [];
  const dirs = new Map();
  const walk = async (entry, path) => {
    if (!dirs.has(path)) dirs.set(path, { pdfs: [], attr: null });
    const children = await readEntryChildren(entry);
    for (const child of children) {
      const name = child?.name;
      if (!isSafeEntryName(name)) continue;
      if (child.isDirectory) {
        await walk(child, `${path}/${name}`);
      } else if (child.isFile) {
        const file = await fileFromEntry(child);
        addBundleFile(dirs.get(path), path, name, file, warnings);
      }
    }
  };
  const rootName = isSafeEntryName(dirEntry.name) ? dirEntry.name : 'folder';
  await walk(dirEntry, rootName);
  return { dirs, warnings };
};

const mergeDirs = (target, source) => {
  source.forEach((node, path) => {
    const prev = target.get(path);
    if (!prev) {
      target.set(path, { pdfs: [...(node.pdfs || [])], attr: node.attr || null });
      return;
    }
    prev.pdfs.push(...(node.pdfs || []));
    if (node.attr) prev.attr = node.attr;
  });
};

const looksLikeClosedFolder = (file) => Boolean(
  file
  && file.size === 0
  && !file.type
  && !String(file.name || '').toLowerCase().endsWith('.pdf'),
);

/**
 * 탐색기 드롭을 폴더와 파일로 가른다.
 * 항목의 webkitGetAsEntry·getAsFile은 이 함수가 호출되는 드롭 처리 중에 바로 읽는다.
 */
export function collectDroppedItems(dataTransfer) {
  const warnings = [];
  const looseFiles = [];
  const directoryReads = [];
  let folderUnreadable = false;
  const items = [...(dataTransfer?.items || [])];

  items.forEach((item) => {
    if (item.kind && item.kind !== 'file') return;
    let entry = null;
    try {
      entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    } catch (_err) {
      entry = null;
    }
    if (entry?.isDirectory) {
      directoryReads.push(collectDirectoryEntry(entry).then(
        (collected) => collected,
        () => {
          folderUnreadable = true;
          warnings.push(`${entry.name || '폴더'} 을 읽지 못했습니다.`);
          return null;
        },
      ));
      return;
    }
    if (entry?.isFile) {
      looseFiles.push(fileFromEntry(entry).catch(() => null));
      return;
    }
    const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
    if (!file || looksLikeClosedFolder(file)) {
      folderUnreadable = true;
      return;
    }
    looseFiles.push(Promise.resolve(file));
  });

  if (!items.length) {
    [...(dataTransfer?.files || [])].forEach((file) => {
      if (!file || looksLikeClosedFolder(file)) folderUnreadable = true;
      else looseFiles.push(Promise.resolve(file));
    });
  }

  return Promise.all([Promise.all(directoryReads), Promise.all(looseFiles)]).then(([dirResults, files]) => {
    const dirs = new Map();
    dirResults.forEach((collected) => {
      if (!collected) return;
      mergeDirs(dirs, collected.dirs);
      warnings.push(...collected.warnings);
    });
    const readableFiles = files.filter(Boolean);
    const unreadable = folderUnreadable && dirs.size === 0;
    return {
      dirs,
      warnings: unreadable ? [] : warnings,
      files: unreadable ? [] : readableFiles,
      folderUnreadable: unreadable,
    };
  });
}

/** 폴더 핸들을 빈 폴더까지 포함해 경로 맵으로 읽는다. */
export async function collectDirectoryHandle(dirHandle) {
  const warnings = [];
  const dirs = new Map();
  const walk = async (handle, path) => {
    if (!dirs.has(path)) dirs.set(path, { pdfs: [], attr: null });
    for await (const [name, child] of handle.entries()) {
      if (!name || name === '.' || name === '..') continue;
      if (child.kind === 'directory') {
        await walk(child, `${path}/${name}`);
      } else {
        const file = await child.getFile();
        const node = dirs.get(path);
        if (name === ATTR_FILE_NAME) node.attr = file;
        else if (name.toLowerCase().endsWith('.pdf')) node.pdfs.push(file);
        else if (!name.startsWith('.')) warnings.push(`${path}/${name} 은 PDF가 아니라 넣지 않았습니다.`);
      }
    }
  };
  await walk(dirHandle, dirHandle.name || 'folder');
  return { dirs, warnings };
};

const authHeaders = () => {
  const headers = subMatFolderActions.actingGclientHeaders({});
  const token = Cookies.get('XSRF-TOKEN');
  if (token) headers['XSRF-Token'] = token;
  const accessToken = sessionStorage.getItem('accessToken');
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return headers;
};

const ROOT_KIND_BY_LABEL = Object.fromEntries(
  Object.entries(subMatFolderActions.ROOT_KIND_LABEL).map(([kind, label]) => [label, kind]),
);

/** 전체 업로드는 맨 위 루트 이름, 한 폴더 업로드는 지금 열린 폴더의 종류를 따른다. */
const rootKindForUploadedPath = (path, parentFolder, pinned) => {
  const top = String(path || '').split('/')[0];
  if (pinned?.has(top) && ROOT_KIND_BY_LABEL[top]) return ROOT_KIND_BY_LABEL[top];
  return parentFolder?.rootKind || '';
};

/**
 * OS에서 고른 폴더를 parentFolder 아래에 만든다.
 * dirs에 빈 폴더가 있으면 파일이 없어도 그 폴더를 만든다.
 * `_속성.txt`가 있으면 속성을 되돌리고, 없으면 폴더와 PDF만 둔다.
 */
export async function uploadFolderBundle({ dispatch, parentFolder, fileList, dirs, pinned, warnings = [] }) {
  const folderDirs = dirs || dirsFromFileList(fileList, warnings);
  if (!folderDirs.size) {
    return { warnings: warnings.length ? warnings : ['폴더 구조를 읽지 못했습니다.'] };
  }

  const idByPath = new Map();
  const paths = [...folderDirs.keys()].sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b, 'ko'));
  for (const path of paths) {
    if (pinned?.has(path)) {
      idByPath.set(path, pinned.get(path));
      continue;
    }
    const parentPath = parentPathOf(path);
    const parentId = parentPath ? idByPath.get(parentPath) : parentFolder.id;
    if (!parentId) {
      warnings.push(`${path} 의 상위 폴더를 만들지 못했습니다.`);
      continue;
    }
    const name = nameOf(path);
    const rootKind = rootKindForUploadedPath(path, parentFolder, pinned);
    if (!rootKind) {
      warnings.push(`${path} 폴더를 만들지 못했습니다.`);
      continue;
    }
    try {
      const created = await dispatch(subMatFolderActions.create({
        id: uuidv4(),
        parentId,
        rootKind,
        name,
      }));
      if (created?.id) idByPath.set(path, created.id);
    } catch (err) {
      if (subMatFolderActions.isDuplicateNameError(err)) {
        const kids = await dispatch(subMatFolderActions.selectChildren(parentId));
        const found = (kids || []).find((folder) => folder.name === name);
        if (found?.id) {
          idByPath.set(path, found.id);
          continue;
        }
      }
      warnings.push(`${path} 폴더를 만들지 못했습니다.`);
    }
  }

  const attrByPath = new Map();
  for (const path of paths) {
    const node = folderDirs.get(path);
    if (!node?.attr) continue;
    try {
      attrByPath.set(path, parseAttrMulti(await node.attr.text()));
    } catch (_err) {
      warnings.push(`${path}/${ATTR_FILE_NAME} 을 읽지 못했습니다.`);
    }
  }

  const uploadedByFolder = new Map();
  const heldByFolder = new Map();
  for (const path of paths) {
    if (pinned?.has(path)) {
      const node = folderDirs.get(path);
      if (node?.pdfs?.length) warnings.push(`${path} 에는 파일을 올리지 않습니다.`);
      continue;
    }
    const folderId = idByPath.get(path);
    const node = folderDirs.get(path);
    if (!folderId || !node) continue;
    const comparisonNames = new Set(
      (attrByPath.get(path)?.comparison || []).map((line) => splitFirst(line)[1]).filter(Boolean),
    );
    const saved = [];
    const held = [];
    for (const pdf of node.pdfs) {
      if (comparisonNames.has(pdf.name)) {
        held.push(pdf);
        continue;
      }
      try {
        const uploaded = await dispatch(subMatFolderActions.uploadFile(folderId, pdf));
        if (uploaded) saved.push({ sourceName: pdf.name, file: uploaded });
      } catch (err) {
        warnings.push(err?.serverResponse?.message || `${path}/${pdf.name} 을 올리지 못했습니다.`);
      }
    }
    uploadedByFolder.set(path, saved);
    heldByFolder.set(path, held);
  }

  for (const path of paths) {
    if (pinned?.has(path)) continue;
    const folderId = idByPath.get(path);
    if (!folderId) continue;
    const multi = attrByPath.get(path) || {};
    const attr = {};
    Object.entries(multi).forEach(([key, values]) => {
      if (values?.length) attr[key] = values[values.length - 1];
    });
    if (attr.role || attr.product || attr.current) {
      const productFolderId = attr.product
        ? idByPath.get(resolveFolderPath(path, attr.product))
        : '';
      const spec = specFromAttr(attr, nameOf(path), productFolderId);
      if (attr.role && !spec) {
        warnings.push(`${path} 속성은 제품 폴더나 인증규격이 없어 넣지 않았습니다.`);
      } else if (spec) {
        try {
          await dispatch(subMatFolderActions.saveFolderSpec(folderId, spec));
        } catch (err) {
          warnings.push(err?.serverResponse?.message || err?.message || `${path} 속성을 저장하지 못했습니다.`);
        }
      }
    }
    if (attr.current) {
      const saved = uploadedByFolder.get(path) || [];
      const match = saved.find((row) => row.sourceName === attr.current || row.file?.fileName === attr.current);
      if (!match) {
        warnings.push(`${path} 의 현재 파일 ${attr.current} 을 찾지 못했습니다.`);
      } else if (match.file?.id && !match.file.isCurrent) {
        try {
          await dispatch(subMatFolderActions.setCurrentFile(folderId, match.file.id));
        } catch (_err) {
          warnings.push(`${path} 의 현재 파일 ${attr.current} 을 표시하지 못했습니다.`);
        }
      }
    }
    await applyBundleExpiry(folderId, path, uploadedByFolder.get(path) || [], multi.expiry, warnings);
    await applyBundleAlarms(folderId, path, multi.alarm, warnings);
    await applyBundleComparisons(
      folderId,
      path,
      uploadedByFolder.get(path) || [],
      heldByFolder.get(path) || [],
      multi,
      warnings,
    );
  }

  for (const path of paths) {
    const folderId = idByPath.get(path);
    const links = attrByPath.get(path)?.link;
    if (!folderId || !links?.length) continue;
    const targetIds = [];
    links.forEach((relative) => {
      const targetId = idByPath.get(resolveFolderPath(path, relative));
      if (targetId) targetIds.push(targetId);
      else warnings.push(`${path} 의 추가 서류 ${relative} 을 찾지 못했습니다.`);
    });
    if (!targetIds.length) continue;
    try {
      await dispatch(subMatFolderActions.replaceFolderLinks(folderId, targetIds));
    } catch (_err) {
      warnings.push(`${path} 의 추가 서류를 붙이지 못했습니다.`);
    }
  }

  return { warnings };
}

const findSaved = (saved, name) => (saved || []).find((row) => row.sourceName === name || row.file?.fileName === name);

const applyBundleExpiry = async (folderId, path, saved, lines, warnings) => {
  for (const line of lines || []) {
    const last = line.lastIndexOf('|');
    const prev = last > 0 ? line.lastIndexOf('|', last - 1) : -1;
    if (prev < 0) continue;
    const name = line.slice(0, prev);
    const day = line.slice(prev + 1, last);
    const noExpiry = line.slice(last + 1).trim().toUpperCase() === 'Y';
    const row = findSaved(saved, name);
    if (!row?.file?.id) {
      warnings.push(`${path} 의 만료일 대상 ${name} 을 찾지 못했습니다.`);
      continue;
    }
    if (!noExpiry && !day) {
      warnings.push(`${path} 의 만료일이 없어 ${name} 은 건너뛰었습니다.`);
      continue;
    }
    try {
      await subMatFolderActions.updateFileExpiryDirect(folderId, row.file.id, {
        endDate: noExpiry ? null : `${day}T00:00:00`,
        validYN: noExpiry,
      });
    } catch (_err) {
      warnings.push(`${path} 의 만료일을 저장하지 못했습니다.`);
    }
  }
};

const applyBundleAlarms = async (folderId, path, lines, warnings) => {
  const alarms = [];
  (lines || []).forEach((line) => {
    const [type, periodText] = splitFirst(line);
    const period = Number(periodText);
    if (!period || period <= 0) return;
    alarms.push({ type, period });
  });
  if (!alarms.length) return;
  try {
    await saveFolderAlarms({ folderId, subMatFolderAlarmEmails: alarms });
  } catch (_err) {
    warnings.push(`${path} 의 만료 알람을 저장하지 못했습니다.`);
  }
};

const applyBundleComparisons = async (folderId, path, saved, held, multi, warnings) => {
  const standardByName = firstByName(multi.comparisonStandard);
  const leadByName = firstByName(multi.comparisonLead);
  const rowsByName = firstByName(multi.comparisonRows);
  for (const line of multi.comparison || []) {
    const [reportName, comparisonName] = splitFirst(line);
    const report = findSaved(saved, reportName);
    const pdf = (held || []).find((file) => file.name === comparisonName);
    if (!report?.file?.id || !pdf) {
      warnings.push(`${path} 의 시험성과대비표 ${comparisonName} 을 짝짓지 못했습니다.`);
      continue;
    }
    try {
      await subMatFolderActions.importTestComparisonFileDirect(folderId, report.file.id, pdf, {
        gstandardId: standardByName[comparisonName],
        leadData: leadByName[comparisonName],
        rows: rowsByName[comparisonName],
      });
    } catch (_err) {
      warnings.push(`${path} 의 시험성과대비표 ${comparisonName} 을 올리지 못했습니다.`);
    }
  }
};

export async function uploadZipBundle(parentFolder, file) {
  const form = new FormData();
  form.append('file', file);
  const response = await window.fetch(`${gcoreServerUrl}/api/subMatFolders/${parentFolder.id}/bundle`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'zip을 올리지 못했습니다.');
  }
  const data = await response.json().catch(() => ({}));
  return { warnings: data.warnings || [] };
}

const saveZipResponse = async (response, filename, fallback) => {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || fallback);
  }
  const blob = await response.blob();
  if (!blob.size) {
    throw new Error(fallback);
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export async function downloadCompanyBundle() {
  const response = await window.fetch(`${gcoreServerUrl}/api/subMatFolders/bundle`, { headers: authHeaders() });
  await saveZipResponse(response, '서류등록.zip', '전체를 받지 못했습니다.');
}

/** 고른 폴더 안이 시스템 루트 이름이면 그 루트에 붙인다. 바깥 폴더 이름은 버린다. */
export function bindCompanyDirs(dirs, rootFolders, warnings) {
  const rootByName = new Map();
  (rootFolders || []).forEach((folder) => {
    if (folder?.isSystem && folder.folderLevel === 0 && folder.name) {
      rootByName.set(folder.name, folder.id);
    }
  });
  let next = dirs;
  const topsOf = (map) => [...new Set([...map.keys()].map((path) => path.split('/')[0]))];
  let tops = topsOf(next);
  const anyRoot = tops.some((name) => rootByName.has(name));
  if (!anyRoot && tops.length === 1) {
    const only = tops[0];
    const childIsRoot = [...next.keys()].some((path) => parentPathOf(path) === only && rootByName.has(nameOf(path)));
    if (childIsRoot) {
      const stripped = new Map();
      next.forEach((node, path) => {
        if (!path.startsWith(`${only}/`)) return;
        stripped.set(path.slice(only.length + 1), node);
      });
      next = stripped;
      tops = topsOf(next);
    }
  }
  const pinned = new Map();
  const drop = [];
  tops.forEach((name) => {
    if (rootByName.has(name)) pinned.set(name, rootByName.get(name));
    else drop.push(name);
  });
  if (drop.length) {
    drop.forEach((name) => warnings.push(`${name} 은 일반문서, 인증서, 성적서, 기타가 아니라 넣지 않았습니다.`));
    const kept = new Map();
    next.forEach((node, path) => {
      if (!drop.includes(path.split('/')[0])) kept.set(path, node);
    });
    next = kept;
  }
  pinned.forEach((_id, name) => {
    if (!next.has(name)) next.set(name, { pdfs: [], attr: null });
  });
  if (!pinned.size) warnings.push('올릴 일반문서, 인증서, 성적서, 기타 폴더가 없습니다.');
  return { dirs: next, pinned };
}

export async function uploadCompanyFolder({ dispatch, fileList, dirs, rootFolders, warnings = [] }) {
  const seeded = await dispatch(subMatFolderActions.ensureCompanyFolders());
  const roots = seeded?.subMatFolders || rootFolders || [];
  const folderDirs = dirs || dirsFromFileList(fileList, warnings);
  const bound = bindCompanyDirs(folderDirs, roots, warnings);
  if (!bound.pinned.size) return { warnings };
  return uploadFolderBundle({
    dispatch,
    parentFolder: { id: null },
    dirs: bound.dirs,
    pinned: bound.pinned,
    warnings,
  });
}

export async function uploadCompanyZip(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await window.fetch(`${gcoreServerUrl}/api/subMatFolders/bundle`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'zip을 올리지 못했습니다.');
  }
  const data = await response.json().catch(() => ({}));
  return { warnings: data.warnings || [] };
}
